import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, useState, useEffect, useCallback, useRef } from 'react'
import { AlertTriangle, ConciergeBell, RefreshCw, WifiOff } from 'lucide-react'
import { ApiRequestError, getCurrentUser, ensureWorkspace, getImpersonationStatus, isNetworkError } from './lib/api'
import Layout from './components/Layout'
import { UserContext, UserContextType } from './hooks/useUser'
import { RequirePermission } from './components/RequirePermission'
import { ChatStateProvider } from './hooks/useChatState'
import { SettingsProvider } from './hooks/useSettings'
import { WhatsNewProvider } from './hooks/useWhatsNew'
import { AnnouncementProvider } from './hooks/useAnnouncement'
import { NotificationCountProvider } from './hooks/useNotificationCount'
import { useThemeEffect } from './hooks/useThemeEffect'
import { recordClientTelemetry } from './lib/clientTelemetry'
import { PushPromptDialog } from './components/PushPromptDialog'
import { TabNotificationBadge } from './components/TabNotificationBadge'
import { OfflineBanner } from './components/OfflineBanner'
import { useOnlineStatus, useRetryOnReconnect } from './hooks/useOnlineStatus'
import AccountLayout from './components/account/AccountLayout'
import { LegacyProfileRedirect, LegacySettingsRedirect } from './components/account/LegacyAccountRedirects'

const Home = lazy(() => import('./pages/Home'))
const Workspace = lazy(() => import('./pages/Workspace'))
const MyApps = lazy(() => import('./pages/MyApps'))
const AppDetail = lazy(() => import('./pages/AppDetail'))
const Settings = lazy(() => import('./pages/Settings'))
const Changelog = lazy(() => import('./pages/Changelog'))
const ChangelogDetail = lazy(() => import('./pages/ChangelogDetail'))
const Guide = lazy(() => import('./pages/Guide'))
const CreatorDashboard = lazy(() => import('./pages/CreatorDashboard'))
const BrowseApps = lazy(() => import('./pages/BrowseApps'))
const Feedback = lazy(() => import('./pages/Feedback'))
const Notifications = lazy(() => import('./pages/Notifications'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Profile = lazy(() => import('./pages/Profile'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminUserDetail = lazy(() => import('./pages/admin/UserDetail'))
const AdminRoles = lazy(() => import('./pages/admin/Roles'))
const AdminIndex = lazy(() => import('./pages/admin/AdminIndex'))
const AdminActivity = lazy(() => import('./pages/admin/Activity'))
const AdminApps = lazy(() => import('./pages/admin/Apps'))
const AdminAppDetail = lazy(() => import('./pages/admin/AppDetail'))
const AdminWorkspaces = lazy(() => import('./pages/admin/Workspaces'))
const AdminPlatformSettings = lazy(() => import('./pages/admin/PlatformSettings'))
const AdminResourceLimits = lazy(() => import('./pages/admin/ResourceLimits'))
const AdminClaudeCode = lazy(() => import('./pages/admin/ClaudeCode'))
const AdminTemplates = lazy(() => import('./pages/admin/Templates'))
const AdminTemplateAnalytics = lazy(() => import('./pages/admin/TemplateAnalytics'))
const AdminSecretFindings = lazy(() => import('./pages/admin/SecretFindings'))
const AppStoreModeration = lazy(() => import('./pages/admin/AppStoreModeration'))
const AnalyticsOverview = lazy(() => import('./pages/admin/analytics/AnalyticsOverview'))
const UserAnalytics = lazy(() => import('./pages/admin/analytics/UserAnalytics'))
const AppAnalytics = lazy(() => import('./pages/admin/analytics/AppAnalytics'))
const AnalyticsActivityFeed = lazy(() => import('./pages/admin/analytics/ActivityFeed'))
const FeatureAdoption = lazy(() => import('./pages/admin/analytics/FeatureAdoption'))
const SystemObservability = lazy(() => import('./pages/admin/analytics/SystemObservability'))
const ChatbotAnalytics = lazy(() => import('./pages/admin/analytics/ChatbotAnalytics'))
const ChangelogVisits = lazy(() => import('./pages/admin/analytics/ChangelogVisits'))
const SurveyResults = lazy(() => import('./pages/admin/analytics/SurveyResults'))

// Component to apply theme effect (must be inside SettingsProvider)
function ThemeApplicator() {
  useThemeEffect()
  return null
}

interface BootstrapErrorState {
  message: string
  retryAfterSeconds: number | null
  /** The request never reached a server — connectivity, not a platform fault. */
  isNetwork: boolean
}

function classifyBootstrapError(error: unknown): BootstrapErrorState {
  if (isNetworkError(error)) {
    return {
      message: error.message,
      // No countdown: useRetryOnReconnect re-bootstraps the moment the
      // connection is back, which beats any fixed timer we could pick.
      retryAfterSeconds: null,
      isNetwork: true,
    }
  }

  if (error instanceof ApiRequestError) {
    return {
      message: error.message,
      retryAfterSeconds: error.status === 429 ? (error.retryAfterSeconds ?? 15) : null,
      isNetwork: false,
    }
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      retryAfterSeconds: /rate limit/i.test(error.message) ? 15 : null,
      isNetwork: false,
    }
  }

  return {
    message: 'Failed to load Code Concierge.',
    retryAfterSeconds: null,
    isNetwork: false,
  }
}

/**
 * Branded first paint.
 *
 * The app shell in index.html covers the gap before the bundle parses; this
 * covers the gap between mount and the bootstrap request resolving. Same mark,
 * so the two hand over without a flash of unrelated UI.
 */
function BootstrapSplash({ offline }: { offline: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[var(--cc-shell-h)] flex-col items-center justify-center gap-4 bg-background px-6 text-center"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
        <ConciergeBell className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} aria-hidden="true" />
      </div>
      <div className="h-1 w-32 overflow-hidden rounded-full bg-muted">
        <div className="h-full w-1/3 animate-cc-progress rounded-full bg-primary motion-reduce:w-full motion-reduce:animate-none" />
      </div>
      <p className="text-sm text-muted-foreground">
        {offline ? 'Waiting for a connection…' : 'Loading Code Concierge…'}
      </p>
    </div>
  )
}

function isBootstrapAuthError(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.status === 401 || error.status === 403)
}

function App() {
  const [user, setUser] = useState<UserContextType | null>(null)
  const [loading, setLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState<BootstrapErrorState | null>(null)
  const [retryCountdown, setRetryCountdown] = useState<number | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { offline } = useOnlineStatus()

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  const bootstrapApp = useCallback(async () => {
    clearRetryTimer()
    setLoading(true)
    setBootstrapError(null)
    setRetryCountdown(null)

    try {
      const [userData, impersonationData] = await Promise.all([
        getCurrentUser(),
        getImpersonationStatus(),
      ])

      setUser({
        email: userData.email,
        name: userData.profile?.displayName || userData.name,
        displayName: userData.profile?.displayName || userData.name,
        avatarUrl: userData.profile?.avatarUrl || null,
        firstName: userData.first_name,
        lastName: userData.last_name,
        services: userData.services,
        isAdmin: userData.is_admin,
        permissions: userData.permissions ?? [],
        roles: (userData.roles ?? []).map((r) => ({
          name: r.name,
          description: r.description,
          isSystem: r.is_system,
        })),
        permissionDetails: userData.permission_details ?? [],
        impersonation: {
          impersonating: impersonationData.impersonating,
          originalAdmin: impersonationData.original_admin,
          targetUser: impersonationData.target_user,
        },
        features: {
          templatesVisibleToUsers: userData.features?.templates_visible_to_users ?? false,
          // Resolve on the server when possible; fall back to a local is_admin || global
          // compute for brief frontend-new/backend-old windows during rollout.
          templatesVisibleToMe:
            userData.features?.templates_visible_to_me !== undefined
              ? userData.features.templates_visible_to_me
              : Boolean(userData.is_admin) || Boolean(userData.features?.templates_visible_to_users),
        },
      })

      try {
        await ensureWorkspace()
      } catch (err) {
        console.error('Failed to ensure workspace:', err)
      }
    } catch (err) {
      console.error('Failed to bootstrap dashboard:', err)

      if (isBootstrapAuthError(err)) {
        clearRetryTimer()
        setBootstrapError(null)
        setRetryCountdown(null)
        setUser(null)
        setLoading(false)
        window.location.href = '/login'
        return
      }

      setUser(null)
      const nextError = classifyBootstrapError(err)
      setBootstrapError(nextError)
      setRetryCountdown(nextError.retryAfterSeconds)
    } finally {
      setLoading(false)
    }
  }, [clearRetryTimer])

  const refreshUser = useCallback(async () => {
    const userData = await getCurrentUser()
    setUser((current) => current ? {
      ...current,
      email: userData.email,
      name: userData.profile?.displayName || userData.name,
      displayName: userData.profile?.displayName || userData.name,
      avatarUrl: userData.profile?.avatarUrl || null,
      firstName: userData.first_name,
      lastName: userData.last_name,
      services: userData.services,
      isAdmin: userData.is_admin,
      permissions: userData.permissions ?? [],
      roles: (userData.roles ?? []).map((role) => ({
        name: role.name,
        description: role.description,
        isSystem: role.is_system,
      })),
      permissionDetails: userData.permission_details ?? [],
      features: {
        templatesVisibleToUsers: userData.features?.templates_visible_to_users ?? false,
        templatesVisibleToMe:
          userData.features?.templates_visible_to_me !== undefined
            ? userData.features.templates_visible_to_me
            : Boolean(userData.is_admin) || Boolean(userData.features?.templates_visible_to_users),
      },
    } : current)
  }, [])

  useEffect(() => {
    void bootstrapApp()

    return () => {
      clearRetryTimer()
    }
  }, [bootstrapApp, clearRetryTimer])

  // Launching the PWA with no signal is the common case on a phone: bootstrap
  // fails, and without this the user is stuck on an error card until they
  // think to tap Retry. Reconnecting should just fix it.
  useRetryOnReconnect(() => {
    if (!user) void bootstrapApp()
  })

  useEffect(() => {
    if (retryCountdown === null || retryCountdown < 0) {
      return
    }

    if (retryCountdown === 0) {
      void bootstrapApp()
      return
    }

    retryTimerRef.current = setTimeout(() => {
      setRetryCountdown((current) => (current === null ? null : current - 1))
    }, 1000)

    return () => {
      clearRetryTimer()
    }
  }, [bootstrapApp, clearRetryTimer, retryCountdown])

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      recordClientTelemetry({
        eventType: 'window_error',
        panel: 'dashboard',
        severity: 'error',
        message: event.message || 'Unhandled browser error',
        details: {
          filename: event.filename,
          line: event.lineno,
          column: event.colno,
        },
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      recordClientTelemetry({
        eventType: 'window_unhandled_rejection',
        panel: 'dashboard',
        severity: 'error',
        message: String(event.reason || 'Unhandled promise rejection'),
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (loading && !user && !bootstrapError) {
    return <BootstrapSplash offline={offline} />
  }

  if (!user && bootstrapError) {
    const networkFailure = bootstrapError.isNetwork || offline
    return (
      <div className="min-h-[var(--cc-shell-h)] bg-background px-6 py-10 text-foreground">
        <div className="mx-auto flex min-h-[calc(var(--cc-shell-h)*0.7)] max-w-lg items-center justify-center">
          <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div
                className={
                  networkFailure
                    ? 'rounded-full bg-muted p-2 text-muted-foreground'
                    : 'rounded-full bg-amber-500/10 p-2 text-amber-600 dark:text-amber-300'
                }
              >
                {networkFailure ? <WifiOff className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              </div>
              <div>
                <h1 className="text-xl font-semibold">
                  {networkFailure
                    ? (offline ? "You're offline" : "Can't reach Code Concierge")
                    : bootstrapError.retryAfterSeconds !== null
                      ? 'Rate limited while loading Code Concierge'
                      : 'Unable to load Code Concierge'}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {networkFailure
                    ? (offline
                      ? 'Code Concierge needs a connection to load your apps and workspaces. We’ll pick up where you left off as soon as you’re back.'
                      : 'Your connection looks fine, so the platform is probably having a moment. Trying again usually works.')
                    : bootstrapError.message}
                </p>
                {retryCountdown !== null && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Retrying automatically in about {Math.max(0, retryCountdown)}s.
                  </p>
                )}
                {networkFailure && offline && (
                  <p className="mt-2 text-sm text-muted-foreground">Retrying automatically.</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void bootstrapApp()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Retrying...' : 'Retry now'}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <UserContext.Provider value={{ ...user, refreshUser }}>
      <SettingsProvider>
        <NotificationCountProvider>
        <TabNotificationBadge />
        {/* Mounted above the routes so it also covers /workspace, which renders
            outside Layout — losing signal mid-session is exactly when a silent
            terminal is most confusing. */}
        <OfflineBanner />
        <ChatStateProvider>
          <WhatsNewProvider>
            <AnnouncementProvider>
            <ThemeApplicator />
            <PushPromptDialog />
            <Suspense fallback={<BootstrapSplash offline={offline} />}>
            <Routes>
              {/* Full-screen workspace page (no Layout) */}
              <Route path="/workspace" element={<Workspace />} />

              {/* Standard pages with sidebar Layout */}
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/apps" element={<MyApps />} />
                    <Route path="/apps/:appName" element={<AppDetail />} />
                    <Route path="/account" element={<AccountLayout />}>
                      <Route index element={<Navigate replace to="/account/profile" />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="appearance" element={<Settings section="appearance" />} />
                      <Route path="workspace" element={<Settings section="workspace" />} />
                      <Route path="notifications" element={<Settings section="notifications" />} />
                      <Route path="connections" element={<Settings section="connections" />} />
                      <Route path="api-access" element={<Settings section="api-access" />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route path="/settings" element={<LegacySettingsRedirect />} />
                    <Route path="/profile" element={<LegacyProfileRedirect />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/changelog/:slug" element={<ChangelogDetail />} />
                    <Route path="/guide" element={<Guide />} />
                    <Route path="/guide/:section" element={<Guide />} />
                    <Route path="/browse" element={<BrowseApps />} />
                    <Route path="/creator-dashboard" element={<CreatorDashboard />} />
                    <Route path="/feedback" element={<Feedback />} />
                    <Route path="/notifications" element={<Notifications />} />
                    {/* Admin routes are always mounted so the path resolves, but
                        each is wrapped in <RequirePermission> with the key its
                        page actually reads. A user without that permission sees a
                        clean "no access" panel instead of a half-empty page that
                        403s on every fetch. The sidebar (filtered via useCan)
                        controls discovery; the backend require_permission gate is
                        the true data boundary. Overview uses "*" — any admin
                        permission — because it aggregates several areas. */}
                    <Route path="/admin" element={<RequirePermission perm="*"><AdminIndex /></RequirePermission>} />
                    <Route path="/admin/users" element={<RequirePermission perm="users.read"><AdminUsers /></RequirePermission>} />
                    <Route path="/admin/users/:userId" element={<RequirePermission perm="users.read"><AdminUserDetail /></RequirePermission>} />
                    <Route path="/admin/roles" element={<RequirePermission perm="roles.read"><AdminRoles /></RequirePermission>} />
                    {/* AdminWorkspaces is an all-apps overview backed by listAllApps() → apps.read. */}
                    <Route path="/admin/workspaces" element={<RequirePermission perm="apps.read"><AdminWorkspaces /></RequirePermission>} />
                    <Route path="/admin/apps" element={<RequirePermission perm="apps.read"><AdminApps /></RequirePermission>} />
                    <Route path="/admin/apps/:appName" element={<RequirePermission perm="apps.read"><AdminAppDetail /></RequirePermission>} />
                    <Route path="/admin/activity" element={<RequirePermission perm="audit.read"><AdminActivity /></RequirePermission>} />
                    <Route path="/admin/secret-findings" element={<RequirePermission perm="secrets.read"><AdminSecretFindings /></RequirePermission>} />
                    <Route path="/admin/app-store-moderation" element={<RequirePermission perm="app_store.moderation.read"><AppStoreModeration /></RequirePermission>} />
                    <Route path="/admin/platform-settings" element={<RequirePermission perm="platform_settings.read"><AdminPlatformSettings /></RequirePermission>} />
                    <Route path="/admin/templates" element={<RequirePermission perm="templates.read"><AdminTemplates /></RequirePermission>} />
                    <Route path="/admin/resource-limits" element={<RequirePermission perm="platform_settings.read"><AdminResourceLimits /></RequirePermission>} />
                    <Route path="/admin/claude-code" element={<RequirePermission perm="config.read"><AdminClaudeCode /></RequirePermission>} />
                    <Route path="/admin/analytics" element={<RequirePermission perm="analytics.read"><AnalyticsOverview /></RequirePermission>} />
                    <Route path="/admin/analytics/users" element={<RequirePermission perm="analytics.read"><UserAnalytics /></RequirePermission>} />
                    <Route path="/admin/analytics/apps" element={<RequirePermission perm="analytics.read"><AppAnalytics /></RequirePermission>} />
                    <Route path="/admin/analytics/activity" element={<RequirePermission perm="analytics.read"><AnalyticsActivityFeed /></RequirePermission>} />
                    <Route path="/admin/analytics/features" element={<RequirePermission perm="analytics.read"><FeatureAdoption /></RequirePermission>} />
                    <Route path="/admin/analytics/system" element={<RequirePermission perm="diagnostics.read"><SystemObservability /></RequirePermission>} />
                    <Route path="/admin/analytics/chatbot" element={<RequirePermission perm="analytics.read"><ChatbotAnalytics /></RequirePermission>} />
                    <Route path="/admin/analytics/templates" element={<RequirePermission perm="analytics.read"><AdminTemplateAnalytics /></RequirePermission>} />
                    <Route path="/admin/analytics/changelog" element={<RequirePermission perm="analytics.read"><ChangelogVisits /></RequirePermission>} />
                    <Route path="/admin/analytics/survey" element={<RequirePermission perm="analytics.read"><SurveyResults /></RequirePermission>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Layout>
              } />
            </Routes>
            </Suspense>
            </AnnouncementProvider>
          </WhatsNewProvider>
        </ChatStateProvider>
        </NotificationCountProvider>
      </SettingsProvider>
    </UserContext.Provider>
  )
}

export default App
